import { memo, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { shuffle } from 'lodash';
import { create } from 'zustand';

import { Images } from '../../../constants/images';
import { SVGIconType } from '../../../constants/svgIcon';
import { colorForStroke } from '../../../constants/Theme/DefaultColors';
import { AppToastErrorOption } from '../../../constants/toast';
import { usePopup } from '../../../context';
import { TtsLocale, Uuid } from '../../../models/Base';
import { StrokeCharacterData } from '../../../models/FileStore';
import {
  EvaluationType,
  GameController,
  GamePlayState,
} from '../../../models/GameControllerStore';
import {
  FingerWritingAnswer,
  FingerWritingSoundMode,
} from '../../../models/GameStore';
import { TextEvaluationWord } from '../../../models/RecordStore';
import { InGameSoundEffects } from '../../../stores/GameControllerStore';
import {
  Character,
  CharacterPreview,
  HandwritingAnswerCardData,
  SvgIcon,
} from '../../Molecules';
import { LeaveGamePopup } from '../../Popups';
import { AnswerCardStatus } from '../GameBlankAnswerCardsGroup';
import { HandwritingGameAnswerCardsGroup } from '../HandwritingGameAnswerCardsGroup';
import { HandwritingGameMode } from '../HandwritingGameQuestionPanel';

import styles from './HandwritingGameMainGroup.module.css';

export type HandwritingBlankInfo = {
  score: number;
  isAnswered?: boolean;
};

type Unit = {
  character: Character;
  wordIndex: number;
  characterIndex: number;
  ttsAudio?: Uuid;
  wordTtsAudio?: Uuid;
};

type HandwritingGameStoreState = {
  gameController: GameController | undefined;
  blankUnits: Unit[] | undefined;
  mode: HandwritingGameMode;
  locale: TtsLocale;
  currentBlankIndex: number;
  currentStrokeIndex: number;
  animatedStrokeIndex: number;
  currentComponent: number | undefined;
  isAnimating: boolean | undefined;
  isWaitingForNext: boolean;
  isEnded: boolean;
  blankInfos: Record<number, HandwritingBlankInfo>;
  cards: HandwritingAnswerCardData[];
  hintUsed: boolean;
  setEvaluation:
    | ((blankInfos: Record<number, HandwritingBlankInfo>) => void)
    | undefined;
  isHintDisabled: boolean;
};

type HandwritingGameStoreAction = {
  setup: (
    gameController: GameController | undefined,
    answer: FingerWritingAnswer,
    strokeData: Record<string, StrokeCharacterData>,
    questionTtsFileId: Uuid | undefined,
    isAssistedGame: boolean | undefined,
    isRevisionGame: boolean | undefined,
    evaluationType: EvaluationType,
  ) => Promise<void>;
  cleanup: () => void;
  refreshCards: () => void;
  onAnimationComplete: (index: number) => void;
  onHint: () => string | undefined;
  onAnswer: (pickedCard: HandwritingAnswerCardData) => boolean;
  updateCardStatus: (
    card: HandwritingAnswerCardData,
    newStatus: AnswerCardStatus,
  ) => void;
  forceUpdateCardStatusesAfterAnswer: (
    pickedCard: HandwritingAnswerCardData,
    isCorrect: boolean,
  ) => void;
};

// Create the store
type Store = HandwritingGameStoreState & HandwritingGameStoreAction;

const INITIAL_VALUE: HandwritingGameStoreState = {
  gameController: undefined,
  blankUnits: undefined,
  mode: HandwritingGameMode.QuickHandwriting,
  locale: TtsLocale.US,
  currentBlankIndex: -1,
  currentStrokeIndex: -1,
  animatedStrokeIndex: -1,
  currentComponent: undefined,
  isAnimating: undefined,
  isWaitingForNext: false,
  isEnded: false,
  blankInfos: {},
  cards: [],
  hintUsed: false,
  setEvaluation: undefined,
  isHintDisabled: true,
};

const getScores = (blankUnitDatas: Unit[] | undefined) => {
  const answerCount =
    blankUnitDatas?.reduce(
      (acc, cur) => acc + cur.character.strokes.length,
      0,
    ) ?? 0;
  const baseScore = answerCount === 0 ? 0 : Math.ceil(60 / answerCount);
  const wrongPenalty = Math.min(baseScore * 2, 20);
  const hintPenalty = Math.min(baseScore, 10);
  const swapPenalty = 15;
  return { baseScore, wrongPenalty, hintPenalty, swapPenalty };
};

export const useHandwritingGameStore = create<Store>((set, get) => {
  const tempDisableHint = () => {
    set({ isHintDisabled: true });
    setTimeout(() => {
      set({ isHintDisabled: false });
    }, 700);
  };

  const next = async () => {
    const {
      gameController,
      blankUnits,
      currentBlankIndex,
      currentStrokeIndex,
      animatedStrokeIndex,
      currentComponent,
      isAnimating,
      isEnded,
    } = get();

    if (animatedStrokeIndex < currentStrokeIndex - 1) {
      return;
    }

    if (isEnded) {
      await gameController?.setGamePlayState(GamePlayState.Score);
      return;
    }

    const currentBlankCharacter = blankUnits?.[currentBlankIndex]?.character;
    if (currentBlankCharacter) {
      let nextComponent: number | undefined;
      if (currentComponent !== undefined) {
        // only change for character with more than 1 component
        const strokes = currentBlankCharacter.strokes;
        if (strokes[strokes.length - 1]?.component !== 0) {
          nextComponent = currentComponent + 1;
          set({ currentComponent: nextComponent });
        }
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      let nextBlankIndex = currentBlankIndex;
      let nextStrokeIndex = currentStrokeIndex;
      if (nextStrokeIndex === currentBlankCharacter.strokes.length) {
        nextBlankIndex += 1;
        nextStrokeIndex = 0;
        if (currentComponent !== undefined) {
          nextComponent = 0;
        }
      }

      let willAnimate: boolean | undefined;
      if (isAnimating !== undefined) {
        willAnimate = true;
      }

      set({
        currentBlankIndex: nextBlankIndex,
        currentStrokeIndex: nextStrokeIndex,
        currentComponent: nextComponent,
        isAnimating: willAnimate,
        isWaitingForNext: false,
        cards: [],
        hintUsed: false,
      });

      if (nextBlankIndex < blankUnits.length) {
        gameController?.initializeNewLap(Infinity);

        if (
          blankUnits?.[currentBlankIndex]?.wordTtsAudio !==
          blankUnits?.[nextBlankIndex]?.wordTtsAudio
        ) {
          gameController?.playAudio(blankUnits?.[nextBlankIndex]?.wordTtsAudio);
        }
      } else {
        onEnd();
      }

      return;
    }

    return;
  };

  const onEnd = () => {
    set({ isEnded: true });
    next().catch(console.error);
  };

  return {
    ...INITIAL_VALUE,
    //
    setup: async (
      gameController: GameController | undefined,
      answer: FingerWritingAnswer,
      strokeData: Record<string, StrokeCharacterData>,
      questionTtsFileId: Uuid | undefined,
      isAssistedGame: boolean | undefined,
      isRevisionGame: boolean | undefined,
      evaluationType: EvaluationType,
    ) => {
      tempDisableHint();
      const blankUnits = answer.words
        .flatMap((word, wordIndex) => {
          // grouping consecutive hints or blanks as an unit group
          // for finding real 'word' (consecutive blanks) in syallable game
          return word.characters.map((character, characterIndex) => {
            const characterData = strokeData[character.fileId ?? ''];
            return {
              character:
                characterData && character.strokeCount
                  ? new Character(characterData)
                  : null,
              wordIndex,
              characterIndex,
              ttsAudio: questionTtsFileId ? undefined : character.ttsAudio,
              wordTtsAudio: isAssistedGame //|| isRevisionGame
                ? word.ttsAudio
                : questionTtsFileId ||
                  answer.soundMode === FingerWritingSoundMode.Silent
                ? undefined
                : word.ttsAudio,
            };
          });
        })
        .filter((unit) => unit.character) as Unit[];

      const setEvaluation = (
        blankInfos: Record<number, HandwritingBlankInfo>,
      ) => {
        const evaluation = answer.words.map(
          (word) =>
            new TextEvaluationWord({
              word: word.word,
              records: [
                {
                  units: word.characters.map((character) => ({
                    unit: character.character,
                    isBlank: character.strokeCount ? true : false,
                  })),
                },
              ],
            }),
        );

        Object.entries(blankInfos).forEach(([blankIndex, blankInfo]) => {
          const unit = blankUnits[blankIndex as unknown as number];
          if (unit) {
            evaluation[unit.wordIndex]?.records[0]?.units[
              unit.characterIndex
            ]?.setScore(blankInfo.score);
          }
        });

        gameController?.setInAppEvaluation({
          word: evaluation,
          type: evaluationType,
        });
      };

      set({
        gameController,
        blankUnits,
        locale: answer.locale,
        mode: HandwritingGameMode.QuickHandwriting,
        currentBlankIndex: 0,
        currentStrokeIndex: 0,
        currentComponent: answer.strokeSettings?.useComponent ? 0 : undefined,
        isAnimating:
          answer.strokeSettings?.useComponent && isAssistedGame
            ? true
            : undefined,
        isEnded: false,
        blankInfos: {},
        cards: [],
        hintUsed: false,
        setEvaluation,
      });

      gameController?.startStage(
        blankUnits.reduce((acc, cur) => acc + cur.character.strokes.length, 0),
      );
      setEvaluation({});
      await gameController?.setGamePlayState(GamePlayState.Run);
      await gameController?.setGamePlayState(GamePlayState.Running);
      if (isAssistedGame /* || isRevisionGame */) {
        gameController?.playAudio(blankUnits[0]?.wordTtsAudio);
      } else {
        gameController?.playAudio(
          questionTtsFileId ?? blankUnits[0]?.wordTtsAudio,
        );
      }
    },
    cleanup: () => {
      set(INITIAL_VALUE);
    },
    refreshCards: () => {
      const {
        blankUnits,
        currentBlankIndex,
        currentStrokeIndex,
        currentComponent,
      } = get();
      tempDisableHint();
      const currentBlankCharacter = blankUnits?.[currentBlankIndex]?.character;
      if (currentBlankCharacter) {
        const cardValues = generateWritingCards(
          currentBlankCharacter,
          4,
          currentStrokeIndex,
          currentComponent,
        );
        set({
          cards: cardValues.map((strokeIndex) => ({
            strokeIndexes: [strokeIndex],
            status: AnswerCardStatus.Pending,
            color:
              currentComponent === undefined
                ? 'black'
                : colorForStroke(
                    strokeIndex -
                      currentBlankCharacter.getComponentStart(
                        currentBlankCharacter.strokes[strokeIndex]?.component ??
                          0,
                      ),
                  ),
          })),
        });
      }
    },
    onAnimationComplete: (index: number) => {
      const {
        blankUnits,
        currentBlankIndex,
        currentComponent,
        isAnimating,
        isWaitingForNext,
      } = get();
      if (isAnimating) {
        const currentBlankCharacter =
          blankUnits?.[currentBlankIndex]?.character;
        if (currentBlankCharacter) {
          if (
            index + 1 ===
            currentBlankCharacter.getComponentStart((currentComponent ?? 0) + 1)
          ) {
            set({
              currentStrokeIndex: currentBlankCharacter.getComponentStart(
                currentComponent ?? 0,
              ),
              isAnimating: false,
            });
          }
        }
      } else {
        set({ animatedStrokeIndex: index });
        if (isWaitingForNext) {
          next().catch(console.error);
        }
      }
    },
    onHint: () => {
      const {
        gameController,
        blankUnits,
        currentBlankIndex,
        currentStrokeIndex,
        cards,
        hintUsed,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];

      if (!hintUsed && currentBlankUnit) {
        const updatedCards = cards.map((card) =>
          card.strokeIndexes[0] === currentStrokeIndex
            ? card
            : {
                ...card,
                status:
                  card.status === AnswerCardStatus.Pending
                    ? AnswerCardStatus.HintCrossed
                    : card.status,
              },
        );
        set({ cards: updatedCards, hintUsed: true });

        const { hintPenalty } = getScores(blankUnits);
        if (gameController?.addScore(-hintPenalty) === 0) {
          onEnd();
        }
        if (
          cards.reduce(
            (acc, cur) =>
              cur.status !== AnswerCardStatus.Empty ? acc + 1 : acc,
            0,
          ) === 1
        ) {
          return 'No incorrect answer here.';
        }
      }
      return undefined;
    },
    onAnswer: (pickedCard: HandwritingAnswerCardData) => {
      const {
        gameController,
        blankUnits,
        currentBlankIndex,
        currentStrokeIndex,
        currentComponent,
        isAnimating,
        isWaitingForNext,
        isEnded,
        blankInfos,
        // updateCardStatus,
        setEvaluation,
        forceUpdateCardStatusesAfterAnswer,
      } = get();
      if (isAnimating || isWaitingForNext || isEnded) {
        return false;
      }

      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      const currentBlankCharacter = currentBlankUnit?.character;

      const isCorrect =
        Boolean(currentBlankUnit) &&
        pickedCard.strokeIndexes[0] === currentStrokeIndex;

      forceUpdateCardStatusesAfterAnswer(pickedCard, isCorrect);

      const currentInfo = blankInfos[currentBlankIndex] ?? {
        score: 100,
      };
      const newBlankInfos = {
        ...blankInfos,
        [currentBlankIndex]: isCorrect
          ? {
              score: currentInfo.score,
              isAnswered:
                currentStrokeIndex + 1 ===
                currentBlankCharacter?.strokes.length,
            }
          : { score: Math.max(currentInfo.score - 30, 0) },
      };
      set({ blankInfos: newBlankInfos });
      setEvaluation?.(newBlankInfos);

      const { baseScore, wrongPenalty } = getScores(blankUnits);

      if (isCorrect) {
        gameController?.playAudio(InGameSoundEffects.correctSelection);
        const newCards = get().cards;
        const removeCross = newCards.map((card) => ({
          // this is for syllable case only, since store update seems has delay
          ...card,
          status:
            card.status === AnswerCardStatus.HintCrossed
              ? AnswerCardStatus.Pending
              : card === pickedCard
              ? isCorrect
                ? AnswerCardStatus.Correct
                : AnswerCardStatus.Wrong
              : card.status,
        }));

        const nextStrokeIndex = currentStrokeIndex + 1;
        const strokeEnd =
          currentComponent === undefined
            ? currentBlankCharacter?.strokes.length
            : currentBlankCharacter?.getComponentStart(currentComponent + 1);
        if (nextStrokeIndex < (strokeEnd ?? 0)) {
          set({ currentStrokeIndex: nextStrokeIndex, hintUsed: false });

          gameController?.addScore(baseScore);
          gameController?.addStagePoint(1);

          gameController?.initializeNewLap(Infinity);

          // if (
          //   blankUnits?.[currentBlankIndex]?.wordTtsAudio !==
          //   blankUnits?.[nextIndex]?.wordTtsAudio
          // ) {
          //   gameController?.playAudio(blankUnits?.[nextIndex]?.wordTtsAudio);
          // }

          const hasCorrectAnswers = removeCross.some(
            (card) =>
              card.status === AnswerCardStatus.Pending &&
              card.strokeIndexes[0] === nextStrokeIndex,
          );
          if (!hasCorrectAnswers) {
            // delay 600 ms to show correct animation, may need a better way though
            new Promise((resolve) => setTimeout(resolve, 600))
              .then(() => {
                set({ cards: [] });
                tempDisableHint();
              })
              .catch(console.error);
          } else {
            set({ cards: removeCross });
          }
        } else {
          set({
            currentStrokeIndex: nextStrokeIndex,
            isWaitingForNext: true,
          });

          gameController?.addScore(baseScore);
          gameController?.addStagePoint(1);
        }
      } else if (gameController?.addScore(-wrongPenalty) === 0) {
        onEnd();
      } else {
        gameController?.playAudio(InGameSoundEffects.wrongSelection);
      }

      return isCorrect;

      // if (isCorrect) {
      //   if (currentStrokeIndex + 1 === totalStrokes) {
      //     // MARK: Correct and Moving to the next character.
      //     set({
      //       currentBlankIndex: currentBlankIndex + 1,
      //       currentStrokeIndex: 0,
      //     });
      //   } else {
      //     // MARK: Correct
      //     // Generate new answer cards while keeping the correct answer
      //     const newAnswerCards = updatedAnswerCards.map((card) =>
      //       card.strokes[0] === currentStrokeIndex
      //         ? card
      //         : {
      //             strokes: [Math.floor(Math.random() * totalStrokes)],
      //             status: AnswerCardStatus.Pending,
      //           },
      //     );
      //     set({ answerCards: newAnswerCards });
      //   }
      //   return true;
      // } else {
      //   const updatedAnswerCards = answerCards.map((card) =>
      //     card === pickedCard
      //       ? { ...card, status: AnswerCardStatus.Wrong }
      //       : card,
      //   );
      //   set({ answerCards: updatedAnswerCards });
      //   // MARK: Wrong
      //   return false;
      // }
    },
    updateCardStatus: (
      card: HandwritingAnswerCardData,
      newStatus: AnswerCardStatus,
    ) => {
      const { cards: answerCards } = get();
      const updatedCards = answerCards.map((c) =>
        c === card ? { ...c, status: newStatus } : c,
      );
      set({ cards: updatedCards });
    },
    forceUpdateCardStatusesAfterAnswer: (
      pickedCard: HandwritingAnswerCardData,
      isCorrect: boolean,
    ) => {
      const { cards: answerCards } = get();
      const updatedCards = answerCards.map((card) => {
        if (card === pickedCard) {
          return {
            ...card,
            status: isCorrect
              ? AnswerCardStatus.Correct
              : AnswerCardStatus.Wrong,
          };
        }

        switch (card.status) {
          case AnswerCardStatus.Correct:
          case AnswerCardStatus.Empty:
            return { ...card, status: AnswerCardStatus.Empty };
          case AnswerCardStatus.Wrong:
          case AnswerCardStatus.Pending:
          case AnswerCardStatus.HintCrossed:
          case AnswerCardStatus.SwapPending:
            return { ...card, status: AnswerCardStatus.Pending };
          default:
            return card;
        }
      });
      set({ cards: updatedCards });
    },
  };
});

export type HandwritingGameMainGroupProps = {
  gamePlayState: GamePlayState;
  gameController?: GameController;
  answer: FingerWritingAnswer;
  strokeData: Record<string, StrokeCharacterData>;
  questionTtsFileId: Uuid | undefined;
  isAssistedGame?: boolean;
  isRevisionGame?: boolean;
  evaluationType: EvaluationType;
};

export const HandwritingGameMainGroup = memo(
  ({
    gamePlayState,
    gameController,
    answer,
    strokeData,
    questionTtsFileId,
    isAssistedGame,
    isRevisionGame,
    evaluationType,
  }: HandwritingGameMainGroupProps) => {
    const {
      blankUnits,
      currentBlankIndex,
      currentStrokeIndex,
      currentComponent,
      isAnimating,
      isWaitingForNext,
      isEnded,
      // blankInfos,
      cards,
      hintUsed,
      isHintDisabled,
      setup,
      cleanup,
      refreshCards,
      onAnimationComplete,
      onHint,
      onAnswer,
      updateCardStatus,
    } = useHandwritingGameStore();
    const { t } = useTranslation();
    const { showPopup, hidePopup } = usePopup();
    const [sizeConfig, setSizeConfig] = useState<{
      previewSize?: number;
      answerCardSize?: number;
    }>();

    useEffect(() => {
      const handleResize = () => {
        const viewportWidth = Math.min(728, window.innerWidth);

        const newSizeConfig =
          viewportWidth > 900
            ? { answerCardSize: viewportWidth / 4.5, previewSize: 400 }
            : viewportWidth > 768
            ? { answerCardSize: viewportWidth / 4.5, previewSize: 280 }
            : viewportWidth > 400
            ? { answerCardSize: viewportWidth / 4.5, previewSize: 180 }
            : { answerCardSize: viewportWidth / 4.5, previewSize: 100 };

        setSizeConfig(newSizeConfig);
      };

      // Initial check
      handleResize();

      // Attach event listener for window resize
      window.addEventListener('resize', handleResize);

      // Cleanup event listener on unmount
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      if (gamePlayState === GamePlayState.Start && !blankUnits) {
        setup(
          gameController,
          answer,
          strokeData,
          questionTtsFileId,
          isAssistedGame,
          isRevisionGame,
          evaluationType,
        ).catch(console.error);
      }
    }, [
      answer,
      blankUnits,
      evaluationType,
      gameController,
      gamePlayState,
      isAssistedGame,
      isRevisionGame,
      questionTtsFileId,
      setup,
      strokeData,
    ]);

    useEffect(() => {
      return cleanup;
    }, [cleanup]);

    const useComponent = answer.strokeSettings?.useComponent ?? false;
    const currentBlankCharacter = blankUnits?.[currentBlankIndex]?.character;

    useEffect(() => {
      if (
        cards.length === 0 &&
        currentBlankCharacter &&
        !isAnimating &&
        !isWaitingForNext &&
        !isEnded
      ) {
        refreshCards();
      }
    }, [
      cards.length,
      currentBlankCharacter,
      isAnimating,
      isWaitingForNext,
      isEnded,
      refreshCards,
    ]);

    const handleHintClick = useCallback(() => {
      const toastMessage = onHint();
      if (toastMessage) {
        toast(t(toastMessage), {
          ...AppToastErrorOption,
          id: 'HintCard',
          style: {
            ...AppToastErrorOption.style,
            marginBottom: '85px',
          },
        });
      }
    }, [onHint, t]);

    if (!blankUnits) {
      return null; // or render a loading state
    }

    // if (currentBlankIndex >= blankUnits.length) {
    //   return null; // or render an end game state
    // }

    let tutorialCard: HandwritingAnswerCardData | undefined;
    if (
      currentBlankCharacter &&
      isAssistedGame &&
      !isAnimating &&
      !isWaitingForNext &&
      !isEnded
    ) {
      tutorialCard = cards.find(
        (card) => card.strokeIndexes[0] === currentStrokeIndex,
      );
    }

    const onClickCancelExit = () => {
      hidePopup();
      gameController
        ?.setGamePlayState(GamePlayState.Resume)
        .catch(console.error);
    };

    return (
      <div className={styles.container}>
        {/* <HandwritingGameQuestionPanel
          answer={answer}
          currentBlankIndex={currentBlankIndex}
          blankInfos={blankInfos}
          mode={HandwritingGameMode.QuickHandwriting}
        /> */}

        {currentBlankCharacter && (
          <div className={styles.characterPreviews}>
            <CharacterPreview
              character={currentBlankCharacter}
              size={sizeConfig?.previewSize}
              getColor={(index, component, isShadow) => {
                if (!useComponent) {
                  return isShadow ? undefined : '#000000';
                }

                if (component < (currentComponent ?? 0)) {
                  return isShadow ? undefined : colorForStroke(component);
                }

                if (component === currentComponent) {
                  if (isShadow && !isAssistedGame) {
                    return '#CCCCCC';
                  }

                  return colorForStroke(
                    index -
                      currentBlankCharacter.getComponentStart(
                        currentBlankCharacter.strokes[index]?.component ?? 0,
                      ),
                    isShadow,
                  );
                }

                return undefined;
              }}
              animatedTo={
                isAnimating
                  ? currentBlankCharacter.getComponentStart(
                      (currentComponent ?? 0) + 1,
                    )
                  : currentStrokeIndex - 1
              }
              onAnimationComplete={onAnimationComplete}
            />
          </div>
        )}

        <div className={styles.answerCards}>
          <HandwritingGameAnswerCardsGroup
            character={currentBlankCharacter}
            cardSize={sizeConfig?.answerCardSize}
            cards={cards}
            onClickCard={onAnswer}
            onUpdateCardStatus={updateCardStatus}
            tutorialCard={tutorialCard}
          />
        </div>

        <div className={styles.buttonBar}>
          <div
            className={styles.back}
            onClick={() => {
              gameController
                ?.setGamePlayState(GamePlayState.Pause)
                .catch(console.error);
              showPopup(
                <LeaveGamePopup
                  onClickCancel={onClickCancelExit}
                  onClickLeave={() => {
                    history.back();
                    hidePopup();
                    // gameController
                    //   ?.setGamePlayState(GamePlayState.Resume)
                    //   .catch(console.error);
                  }}
                />,
                { backdropCallback: () => onClickCancelExit() },
              );
            }}
          >
            <SvgIcon
              name={SVGIconType.Logout}
              width={37}
              height={37}
              color={'white'}
            />
          </div>
          {!isEnded && (
            <>
              <HintButton
                onClick={handleHintClick}
                disabled={isAssistedGame || hintUsed || isHintDisabled}
                scoreCost={getScores(blankUnits).hintPenalty}
              />
            </>
          )}
        </div>
      </div>
    );
  },
);

const HintButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  scoreCost: number;
}> = ({ onClick, disabled, scoreCost = 5 }) => {
  const { t } = useTranslation();
  return (
    <div
      className={!disabled ? styles.hint : `${styles.hint} ${styles.disabled}`}
      onClick={!disabled ? onClick : undefined}
    >
      <div className={styles.icon}>
        <img src={Images.JOYAJOY_logo} width={40} height={40} />
      </div>
      <div className={styles.text}>
        <span className={styles.label}>{t('Hint')}</span>
        <span className={styles.scoreDeduction}>-{scoreCost}</span>
      </div>
    </div>
  );
};

// move to const
export const generateWritingCards = (
  character: Character,
  numberPerHands: number,
  currentStrokeIndex: number,
  currentComponent: number | undefined,
) => {
  const strokeCount = character.strokes.length;
  const end =
    currentComponent === undefined
      ? strokeCount
      : character.getComponentStart(currentComponent + 1);

  const result = [];
  const addedStrokes = new Set(); // Set to keep track of added strokes

  // Generate an array of stroke numbers from currentStrokeIndex to fill up numberPerHands
  for (let i = 0; i < numberPerHands; i++) {
    if (currentStrokeIndex + i < end) {
      result.push(currentStrokeIndex + i);
      addedStrokes.add(currentStrokeIndex + i); // Add the stroke to the set
    } else {
      let randomStroke = Math.floor(Math.random() * strokeCount);
      while (
        addedStrokes.size < strokeCount &&
        addedStrokes.has(randomStroke)
      ) {
        randomStroke = Math.floor(Math.random() * strokeCount);
      }
      result.push(randomStroke);
      addedStrokes.add(randomStroke); // Add the random stroke to the set
    }
  }

  return shuffle(result);
};
